import {combineEpics, Epic, StateObservable} from 'redux-observable';
import {debounceTime, filter, mergeMap, map, switchMap} from 'rxjs/operators';
import {isActionOf} from 'typesafe-actions';
import {RootState} from '../../../reducer';
import {SearchFeature, SearchResult} from '../../../interfaces/SearchFeature';
import _ from 'lodash';
import {EMPTY, forkJoin, of} from 'rxjs';
import {
    fetchSearchOptionsBySearchOptionSourceOnStoreNewSearchValue,
    storeNewSearchResult,
    storeNewSearchValue
} from '../../../scenes/MapScene/actions/reducers/filterAndSearch';
import {
    mapSceneDataClearMapFilteredData,
    mapSceneDataUpdateDataSourceFilteredMapData,
} from '../../../scenes/MapScene/actions/reducers/data';
import {FeatureCollection} from 'geojson';
import {fetchSearchOptions} from '../../../services/ViewerApiService';
import {viewerApiServiceSearchOptionsCompleted} from '../../../services/ViewerApiService/actions/searchOptions';
import {isVersionMetadata} from '../../../interfaces/Metadata';

const searchFilterFeaturesOnStoreNewSearchValue: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(storeNewSearchValue)),
        debounceTime(1000),
        map(({payload: {searchValue}}) => {
            const lowedQuery = searchValue.toLowerCase();

            if (lowedQuery.length === 0) {
                return storeNewSearchResult(null);
            }

            const searchFeaturesFound: SearchResult[] = [];
            for (const sourcesKey in state$.value.mapScene.data.sources) {
                if (!state$.value.mapScene.data.sources.hasOwnProperty(sourcesKey)) {
                    continue;
                }
                const dataSource = state$.value.mapScene.data.sources[sourcesKey];

                if (dataSource.isActive && !dataSource.searchOptionSource) {
                    const sourceMatchedFeatures = _.filter(dataSource.searchFeatures, (item: SearchFeature) => {
                        const stringSearchValue: string =
                            typeof item.displayValue !== 'string' ? item.displayValue.toString() : item.displayValue;
                        return stringSearchValue.toLowerCase().indexOf(lowedQuery) > -1;
                    });

                    const searchResult: SearchResult = {
                        id: dataSource.id,
                        name: dataSource.name,
                        results: _.take(sourceMatchedFeatures, 150),
                    };
                    searchFeaturesFound.push(searchResult);
                }
            }

            return fetchSearchOptionsBySearchOptionSourceOnStoreNewSearchValue(
                searchValue,
                searchFeaturesFound,
            );
        }),
    );

const fetchSearchOptionsBySearchOptionSource: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(fetchSearchOptionsBySearchOptionSourceOnStoreNewSearchValue)),
        switchMap(({payload: {searchValue, searchResults}}) => {

            const observables = Object.entries(state$.value.mapScene.data.sources)
                .filter(([, datasource]) => datasource.isActive && datasource.searchOptionSource)
                .map(([, dataSource]) => {
                    if (dataSource.searchOptionSource == null || !isVersionMetadata(dataSource.metadata)) {
                        return of();
                    }
                    return fetchSearchOptions(dataSource.searchOptionSource, dataSource.metadata, searchValue).pipe(
                        filter(isActionOf(viewerApiServiceSearchOptionsCompleted)),
                        map(response => response.payload.searchFeatures),
                        map(searchFeatures => ({
                            id: dataSource.id,
                            name: dataSource.name,
                            results: searchFeatures
                        }))
                    );
                });

            if (observables.length === 0) {
                return of(storeNewSearchResult({
                    identifier: Date.now(),
                    searchResults: searchResults,
                }));
            }

            return forkJoin(observables).pipe(
                map((results: SearchResult[]) => {
                    results.forEach((result: SearchResult) => searchResults.push(result));
                    return storeNewSearchResult({
                        identifier: Date.now(),
                        searchResults: searchResults
                    });
                })
            );
        })
    );

const updateFilteredMapDataOnStoreNewSearchResult: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(storeNewSearchResult)),
        filter((action) => action.payload.searchResults !== null),
        mergeMap(({payload: {searchResults}}) => {
            const layerSearchResults = searchResults!.searchResults;
            return of(...layerSearchResults).pipe(
                map((searchResultSet: SearchResult) => {
                    const dataSource = state$.value.mapScene.data.sources[searchResultSet.id];
                    const filterIDs = _.map(searchResultSet.results, 'id');

                    if (dataSource.dataLoadMethod === 'GeoJSON') {
                        const filteredFeatures = _.cloneDeep(
                            state$.value.mapScene.data.sources[searchResultSet.id].layerMapData!
                                .features as FeatureCollection['features'],
                        ).filter((feature) => filterIDs.some((s) => feature.properties?.id.includes(s)));

                        return mapSceneDataUpdateDataSourceFilteredMapData(searchResultSet.id, {
                            features: filteredFeatures,
                            type: 'FeatureCollection',
                        });
                    } else if (dataSource.dataLoadMethod === 'VectorTiles') {
                        const layerMapData = _.cloneDeep(dataSource.layerMapData);
                        let filtered = {};
                        if (layerMapData) {
                            filtered = Object.keys(layerMapData)
                                .filter((key) => {
                                    return filterIDs.includes(parseInt(key, 10));
                                })
                                .reduce<Record<string, unknown>>((obj, key) => {
                                    obj[key] = layerMapData[key];
                                    return obj;
                                }, {});
                        }

                        return mapSceneDataUpdateDataSourceFilteredMapData(searchResultSet.id, filtered);
                    }

                    return EMPTY;
                }),
            );
        }),
    );

const clearFilteredMapDataOnStoreNewSearchResultEmpty: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(storeNewSearchResult)),
        filter((action) => action.payload.searchResults === null),
        map(() => mapSceneDataClearMapFilteredData()),
    );

const searchPaneEpics: Epic = combineEpics(
    clearFilteredMapDataOnStoreNewSearchResultEmpty,
    searchFilterFeaturesOnStoreNewSearchValue,
    fetchSearchOptionsBySearchOptionSource,
    updateFilteredMapDataOnStoreNewSearchResult,
);

export default searchPaneEpics;
